import React, { useCallback, useEffect, useState } from 'react';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Line,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { formatDate } from '@lib/utils.ts';
import { getPayoutDashboardChart } from '@src/services/reports';
import { toast } from 'react-toastify';

interface TradeData {
  date: string;
  // dxFeed: number;
  // ninjaTrader: number;
  rithmic: number;
  values?: string;
}

type TradePayload = {
  value: number;
  values?: string;
  payload?: TradeData;
  color?: string;
  dataKey?: string;
};

interface TooltipProps {
  active?: boolean;
  payload?: TradePayload[];
}

const CustomTooltip: React.FC<TooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className={
          'bg-card-stat p-2.5 rounded-1 text-xs border-1 border-[#3D3D3D] rounded-[8px] flex flex-col items-center min-w-[135px] gap-y-1'
        }
        style={{
          boxShadow: '0px 7px 13px 0px #DC342E14',
        }}
      >
        {/*<p className={'text-[#DC342E] text-xl'}>
          {payload[0]?.payload?.ninjaTrader}
        </p>*/}
        {/*<p className={'text-[#255589] text-xl'}>*/}
        {/*  {payload[0]?.payload?.dxFeed}*/}
        {/*</p>*/}
        <p className={'text-[#B6D72C] text-xl'}>
          {payload[0]?.payload?.rithmic}
        </p>
        <p className={'text-[#999999] text-[10px]'}>
          {formatDate(payload[0]?.payload?.date)}
        </p>
      </div>
    );
  }

  return null;
};

interface CustomLegendProps {
  payload?: TradePayload[];
}

const CustomLegend: React.FC<CustomLegendProps> = ({ payload }) => {
  if (!payload) return null;

  const backgroundColors: { [key: string]: string } = {
    ninjaTrader: '#DC342E',
    // dxFeed: '#255589',
    rithmic: '#B6D72C',
  };

  return (
    <div className={'flex justify-between items-center mb-5'}>
      <p className={'text-[22px] font-semibold'}>Users By Platform</p>
      <div className={'flex gap-x-6 p-2.5'}>
        {payload.map((entry, index) => (
          <div key={index} className={'flex items-center gap-x-1.5'}>
            <div
              className={`w-3 h-3 rounded-full`}
              style={{
                backgroundColor: backgroundColors[entry.dataKey || ''],
              }}
            />
            <p>{entry?.payload?.values}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

interface CartActivityProps {
  startDate?: string | null;
  endDate?: string | null;
}

export const UserByPlatform: React.FC<CartActivityProps> = ({
  startDate,
  endDate,
}) => {
  const [chartData, setChartData] = useState<TradeData[]>([]);

  const fetchChartData = useCallback(async () => {
    try {
      const response = await getPayoutDashboardChart({
        start_date: startDate || null,
        end_date: endDate || null,
      });

      const transformedData: TradeData[] = response.months.map(
        (month, index) => {
          // const ninjaTrader = response.series.find(
          //   (s) => s.platform === 'NinjaTrader'
          // );
          // const dxFeed = response.series.find((s) => s.platform === 'DxFeed');
          const rithmic = response.series.find((s) => s.platform === 'Rithmic');

          return {
            date: month,
            // ninjaTrader: ninjaTrader?.data[index] || 0,
            // dxFeed: dxFeed?.data[index] || 0,
            rithmic: rithmic?.data[index] || 0,
          };
        }
      );

      setChartData(transformedData);
    } catch (error) {
      console.error('Failed to fetch payout chart data:', error);
      toast.error(
        'Failed to load users by platform data. Please try again later.'
      );
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchChartData();
  }, [fetchChartData]);

  return (
    <div className={'flex w-full flex-col'}>
      <ResponsiveContainer height={600} width="100%">
        <ComposedChart
          data={chartData}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5,
          }}
        >
          <Tooltip content={<CustomTooltip />} />
          <Legend
            align="center"
            content={<CustomLegend />}
            layout="horizontal"
            verticalAlign="top"
          />
          <CartesianGrid
            stroke="#919EAB3D"
            strokeDasharray="2 2"
            vertical={false}
          />
          <XAxis
            axisLine={false}
            dataKey="date"
            fontSize={12}
            padding={{ left: 0, right: 0 }}
            stroke="#919EAB"
            tickFormatter={(date) => format(parseISO(date), 'MMM')}
            tickLine={false}
          />
          <YAxis
            axisLine={false}
            domain={[0, 100]}
            fontSize={12}
            stroke="#919EAB"
            tickLine={false}
            yAxisId="left"
          />
          <defs>
            <linearGradient id="barGradientRed" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#DC342E" />
              <stop offset="100%" stopColor="rgba(220, 52, 46, 0.16)" />
            </linearGradient>
          </defs>
          {/*<Area
            dataKey="ninjaTrader"
            fill="url(#barGradientRed)"
            stroke="#DC342E"
            type={'monotone'}
            values={'NinjaTrader'}
            yAxisId="left"
          />*/}
          {/* <Line
            dataKey="dxFeed"
            dot={false}
            stroke="#1890FF"
            type={'monotone'}
            values={'DxFeed'}
            yAxisId="left"
          /> */}
          <Line
            dataKey="rithmic"
            dot={false}
            stroke="#B6D72C"
            type={'monotone'}
            values={'Rithmic'}
            yAxisId="left"
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};
